import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {AcquisitionClickParam} from "@devour/client";

export interface AcquisitionSignals {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    referrer?: string;
    /** Path + query of the page they landed on. Raw; resolution to a game or surface is read-side. */
    landingPage?: string;
    /**
     * The ad platform's click id: the query parameter's name, and its value. Both or neither — the
     * server discards a half pair, because a value with no field name cannot be uploaded anywhere.
     */
    clickParam?: AcquisitionClickParam;
    clickValue?: string;
}

/**
 * The acquisition signals captured at landing, for any signup submit call.
 *
 * Every signup path needs the same fields and the same expiry check, and
 * the defect this exists to fix is a submit path reading `searchParams` — which
 * is empty by the time the user returns from their email. Reading the persisted
 * slice through one hook is what keeps the next login surface from
 * reintroducing that.
 *
 * Returns an empty object once expired, so a stale campaign cannot be credited
 * for a signup a month later.
 */
export function useGetAcquisitionSignals(): AcquisitionSignals {
    const utmSource = useSelector((store: IStore) => store.acquisitionStore.utmSource);
    const utmMedium = useSelector((store: IStore) => store.acquisitionStore.utmMedium);
    const utmCampaign = useSelector((store: IStore) => store.acquisitionStore.utmCampaign);
    const referrer = useSelector((store: IStore) => store.acquisitionStore.referrer);
    const landingPage = useSelector((store: IStore) => store.acquisitionStore.landingPage);
    const clickParam = useSelector((store: IStore) => store.acquisitionStore.clickParam);
    const clickValue = useSelector((store: IStore) => store.acquisitionStore.clickValue);
    const expiry = useSelector((store: IStore) => store.acquisitionStore.acquisitionExpiry);

    if (!expiry || Date.now() >= expiry) {
        return {};
    }

    return {utmSource, utmMedium, utmCampaign, referrer, landingPage, clickParam, clickValue};
}
